import type { ClientClassificationInput } from "@repo/shared/types";
import {
  usePublicClientClassificationPreviousAnswer,
  usePublicClientClassificationValue,
  useSavePublicClientClassification,
} from "@/lib/hooks/use-public-flow/use-client-classification";
import { log } from "@/lib/logger/browser-logger";
import { ClientClassificationWidget } from "@/pages/clients/detail/flows/instance/client-classification-widget";
import { usePublicFlowActions } from "../public-flow-context";
import type { ComponentStyleType } from "./types";

type PublicClientClassificationRendererProps = {
  component: { config?: string };
  clientType: "Person" | "Company";
  clientName: string;
  isCompleted: boolean;
  hasValidationError?: boolean;
};

export function ClientClassificationRenderer({
  component,
  clientType,
  clientName,
  isCompleted,
  hasValidationError,
}: PublicClientClassificationRendererProps) {
  const actions = usePublicFlowActions();
  const { inputData, isLoading: valueLoading } = usePublicClientClassificationValue();
  const { previousAnswer, isLoading: prevLoading } = usePublicClientClassificationPreviousAnswer();
  const save = useSavePublicClientClassification();

  let config: { style?: string; title?: string; description?: string } = {};
  try {
    config = component.config ? JSON.parse(component.config) : {};
  } catch {
    // Invalid config
  }

  const handleChange = (input: ClientClassificationInput) => {
    save.mutate(input, {
      onSuccess: (result) => {
        if (result.success && result.calculatedData) {
          try {
            const calculated = JSON.parse(result.calculatedData);
            if (calculated.classificationType) {
              actions.setFieldValue("__classification_type__", calculated.classificationType);
            }
          } catch {
            /* ignore */
          }
        }
      },
    });
  };

  const handleUsePreviousAnswer = previousAnswer
    ? async () => {
        try {
          const prevInput: ClientClassificationInput = JSON.parse(previousAnswer.inputData);
          save.mutate(prevInput, {
            onSuccess: (result) => {
              if (result.success && result.calculatedData) {
                try {
                  const calculated = JSON.parse(result.calculatedData);
                  if (calculated.classificationType) {
                    actions.setFieldValue("__classification_type__", calculated.classificationType);
                  }
                } catch {
                  /* ignore */
                }
              }
            },
          });
        } catch (error) {
          log.error({ err: error }, "Failed to use previous classification answer");
        }
      }
    : undefined;

  if (valueLoading || prevLoading) return null;

  return (
    <ClientClassificationWidget
      clientType={clientType}
      clientName={clientName}
      inputData={inputData}
      onChange={handleChange}
      disabled={isCompleted}
      previousAnswer={previousAnswer}
      onUsePreviousAnswer={handleUsePreviousAnswer}
      style={(config.style as ComponentStyleType) || "none"}
      title={config.title}
      description={config.description}
      hasValidationError={hasValidationError}
    />
  );
}
